import { ReactElement, useEffect, useState } from "react";
import { useAtomValue, useSetAtom } from "jotai";
import { Box, Grid, Stack } from "@mui/material";
import { useTranslation } from "react-i18next";
import { showHomePageAtom, userPreferencesAtom } from "atoms";
import { useOIDCLogin } from "shared/hooks/useOIDCLogin";
import ProfileSettingsWrapper from "shared/components/ProfileSettingsWrapper";
import {
  PrimaryButton,
  ProfileIconAndMenu,
  NavbarPanel,
  HelpMenu,
} from "@web-applications/allinga-web-components";
import IISLogo from "assets/IISLogo.svg";
import { PATHS } from "shared/constants/paths";
import { runtimeConfig } from "@/shared/config/runtime-config-loader";

function Navbar(): ReactElement {
  const oidcAuth = useOIDCLogin();
  const { auth } = oidcAuth;
  const { i18n, t } = useTranslation();
  const setShowHomePage = useSetAtom(showHomePageAtom);

  const [showProfileSettings, setShowProfileSettings] = useState(false);
  const userPreferences = useAtomValue(userPreferencesAtom);
  const { avatar, username, firstName, lastName } = userPreferences;
  const name =
    firstName || lastName ? `${firstName} ${lastName}` : username || "User";

  useEffect(() => {
    // default language to English if nothing found in userPreferences or if there's an error fetching userPreferences
    sessionStorage.setItem("language", userPreferences.language || "en");
    i18n.changeLanguage(userPreferences.language || "en");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userPreferences]);

  const handleLogin = async () => {
    setShowHomePage(false);
    await auth.signinRedirect();
  };

  const handleLogout = () => {
    setShowHomePage(true);
    auth.signoutRedirect();
  };

  const handleProfileSettings = () => {
    setShowProfileSettings(true);
  };

  return (
    <Grid
      id="navbar"
      container
      justifyContent="space-between"
      alignItems="center"
      padding={
        auth.isAuthenticated
          ? "0"
          : runtimeConfig.ttsOnPrem
            ? "0px 24px"
            : "20px 24px"
      }
      sx={{
        position: "sticky",
        zIndex: 3,
        top: 0,
        backgroundColor: auth.isAuthenticated ? "#FFFFFF" : "#ebf7f7",
      }}
    >
      {!auth.isAuthenticated && (
        <Grid item>
          <Stack direction="row" gap={5} alignItems="center">
            <Box>
              <a href="/" rel="noopener noreferrer">
                <Box component="img" src={IISLogo} alt="IIS" />
              </a>
            </Box>
          </Stack>
        </Grid>
      )}

      {auth.isAuthenticated ? (
        // Show menus for logged-in user
        <NavbarPanel
          leftGrid={[]}
          rightGrid={
            <Stack
              direction="row"
              alignItems="center"
              gap={1}
              sx={{ marginRight: "8px" }}
            >
              <HelpMenu
                RELEASE_NOTES_URL={PATHS.releaseNotes}
                releaseNotesText={t("helpMenu.releaseNotes")}
                additionalHelpMenuOptions={[
                  {
                    name: t("ssml.headline"),
                    url: PATHS.ssmlGuide,
                    id: "documentation-menu-item",
                  },
                  {
                    name: t("ssml.cheatSheet"),
                    url: PATHS.ssmlCheatSheet,
                    id: "cheat-sheet-menu-item",
                  },
                ]}
              />
              <ProfileIconAndMenu
                userName={name.trim()}
                avatarIcon={avatar || ""}
                profileSettingsText={t("profileSettings.title")}
                logOutText={t("profileMenu.logOut")}
                handleLogOutClick={handleLogout}
                handleProfileSettingsClick={handleProfileSettings}
                versionInfoText={t("helpMenu.versionInfo")}
                appName="Allinga Voice"
                appVersion={runtimeConfig.appVersion}
                privacyText={t("legal.privacy")}
                imprintText={t("legal.imprint")}
                THIRD_PARTY_NOTICES_URL={PATHS.thirdPartyNotices}
                thirdPartyNoticesText={t("legal.thirdPartyNotices")}
                COPYRIGHT_URL={PATHS.copyright}
                copyrightText={t("legal.copyright")}
                shouldOpenInNewTab
                id="profile-menu"
              />
            </Stack>
          }
        />
      ) : (
        // Render login button for users who are not logged in
        !runtimeConfig.ttsOnPrem && (
          <PrimaryButton
            text={t("buttons.logIn")}
            onSubmit={handleLogin}
            id="navbar-login-button"
          />
        )
      )}
      {showProfileSettings && (
        <ProfileSettingsWrapper
          handleCloseDialog={() => setShowProfileSettings(false)}
        />
      )}
    </Grid>
  );
}

export default Navbar;
